import * as React from 'react';

/** A fee rule (Receipt-Web-Server api/fees.py): how a delivery or service fee comes from an order's total. */
export type FeeRule =
  | { type: 'none'; round_to?: number }
  | { type: 'fixed'; amount: number; round_to?: number }
  | { type: 'per_item'; amount: number; round_to?: number }
  | { type: 'percent'; percent: number; min?: number | null; max?: number | null; round_to?: number }
  | { type: 'tiers'; tiers: { from: number; amount?: number; percent?: number }[]; round_to?: number };

/** Whether the business takes orders from its catalog, and how (api/orders.py public_ordering). */
export type Ordering = {
  enabled: boolean; pickup?: boolean; delivery?: boolean; delivery_fee?: FeeRule; min_order?: number; whatsapp?: string; message?: string;
  /** A business that buys what each customer asks for: its service fee, catalog prices as estimates, and requests
   * for things the catalog doesn't have. */
  service_fee?: FeeRule; service_fee_label?: string; estimated_prices?: boolean; free_text?: boolean;
  /** Paying online when ordering (the business's provider: Mercado Pago…): offered, and required never, for delivery
   * or always; how the customer pays there, under the button. */
  pay_online?: boolean; pay_online_required?: 'never' | 'delivery' | 'always'; pay_online_note?: string;
  /** Customers pay orders with their prepaid balance (their card's code on their phone proves it's theirs). */
  balance?: boolean;
  /** Customers can have loyalty rewards to use on their orders: the checkout shows them once it recognizes them. */
  rewards?: boolean;
};

/** The fee for an order of `subtotal` with `pieces` items: the same math as the server, to show it before sending. */
export const computeFee = (rule: FeeRule | undefined, subtotal: number, pieces = 0): number => {
  if (!rule) return 0;
  let fee = 0;
  if (rule.type === 'fixed') fee = rule.amount;
  else if (rule.type === 'per_item') fee = rule.amount * pieces;
  else if (rule.type === 'percent') {
    fee = (subtotal * rule.percent) / 100;
    if (rule.min != null) fee = Math.max(fee, rule.min);
    if (rule.max != null) fee = Math.min(fee, rule.max);
  } else if (rule.type === 'tiers') {
    const tier = [...rule.tiers].sort((a, b) => a.from - b.from).filter(t => subtotal >= t.from).pop();
    fee = !tier ? 0 : tier.percent != null ? (subtotal * tier.percent) / 100 : tier.amount ?? 0;
  }
  if (rule.round_to && fee > 0) fee = Math.ceil(Math.round(fee * 100) / 100 / rule.round_to) * rule.round_to;
  return Math.round(fee * 100) / 100;
};

/**
 * Where the fee drops next (free delivery from $105): how much more the order needs and the fee from there; null when it
 * can't drop (no fee now, or a rule without amounts to reach).
 */
export const nextFeeStep = (rule: FeeRule | undefined, subtotal: number, pieces = 0) => {
  if (!rule || rule.type !== 'tiers') return null;
  const now = computeFee(rule, subtotal, pieces);
  if (now <= 0) return null;
  const step = [...rule.tiers].sort((a, b) => a.from - b.from).find(t => t.from > subtotal && computeFee(rule, t.from, pieces) < now);
  return step ? { from: step.from, missing: Math.round((step.from - subtotal) * 100) / 100, fee: computeFee(rule, step.from, pieces) } : null;
};

/** The rule charges a fee on smaller orders (so reaching the free range is worth telling). */
export const hasPaidRange = (rule: FeeRule | undefined) =>
  !!rule && rule.type === 'tiers' && rule.tiers.some(t => (t.amount ?? 0) > 0 || (t.percent ?? 0) > 0);

export type CartOption = { id: number; name: string; price_delta: number; group: string };
/** A line of the cart: a product with its chosen options; the same product and options again adds quantity. A request
 * for something the catalog doesn't have has no product (and no price yet): its name. */
export type CartLine = { key: string; product: number | null; name: string; unit: number; options: CartOption[]; quantity: number };

export const lineKey = (product: number | null, options: CartOption[], name = '') => (product == null
  ? `pide-${name.trim().toLowerCase()}`
  : [product, ...options.map(o => o.id).sort((a, b) => a - b)].join('-'));

/** The cart of one business's catalog, kept in this browser until the order is sent. */
export const useCart = (slug: string) => {
  const storageKey = `true.cart.${slug}`;
  const [lines, setLines] = React.useState<CartLine[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '[]');
    } catch {
      return [];
    }
  });
  React.useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(lines));
    } catch { /* private window: the cart lives while the page is open */ }
  }, [storageKey, lines]);

  const add = React.useCallback((line: Omit<CartLine, 'key'>) => setLines(current => {
    const key = lineKey(line.product, line.options, line.name);
    const existing = current.find(l => l.key === key);
    return existing
      ? current.map(l => (l.key === key ? { ...l, quantity: Math.min(99, l.quantity + line.quantity) } : l))
      : [...current, { ...line, key }];
  }), []);
  const setQuantity = React.useCallback((key: string, quantity: number) =>
    setLines(current => (quantity <= 0 ? current.filter(l => l.key !== key) : current.map(l => (l.key === key ? { ...l, quantity: Math.min(99, quantity) } : l)))), []);
  const clear = React.useCallback(() => setLines([]), []);

  const count = lines.reduce((sum, l) => sum + l.quantity, 0);
  const subtotal = Math.round(lines.reduce((sum, l) => sum + l.unit * l.quantity, 0) * 100) / 100;
  /** Lines still to be priced by the business (requests outside the catalog). */
  const unpriced = lines.filter(l => l.product == null).length;
  return { lines, add, setQuantity, clear, count, subtotal, unpriced };
};
